import { useCallback, useEffect, useRef, useState } from "react";
import { Info, Loader2, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { appDialogPrimaryButton } from "@/components/ui/app-dialog";
import { getSelectionAvailableGames, SelectionGame } from "@/Api/gameBankSelections";
import { cn } from "@/lib/utils";
import { pad2 } from "../GameBank/shared";

const PER_PAGE = 50;

interface GamePickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lotteryId: number;
  dezenas: number;
  /** Seleção em edição: os jogos dela contam como disponíveis (ela mesma já os escolheu) */
  selectionUuid?: string;
  /** Jogos já escolhidos na tela (em qualquer bolão): não aparecem na lista */
  excludeIds: number[];
  /** Quantos jogos podem ser marcados (vagas livres do bolão; 1 quando é "Trocar jogo") */
  maxSelect: number;
  /** Texto do botão e do aviso ("Bolão 1") */
  bolaoLabel: string;
  /** "Trocar jogo" substitui um jogo; "add" completa as vagas livres */
  mode: "add" | "replace";
  onConfirm: (games: SelectionGame[]) => void;
}

/**
 * Painel lateral "Banco de jogos · N dezenas": mostra só jogos que ainda não foram usados em nenhuma seleção
 * (nem escolhidos em outro bolão da tela) e deixa marcar os que entram no bolão.
 */
export function GamePickerSheet({
  open,
  onOpenChange,
  lotteryId,
  dezenas,
  selectionUuid,
  excludeIds,
  maxSelect,
  bolaoLabel,
  mode,
  onConfirm,
}: GamePickerSheetProps) {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [games, setGames] = useState<SelectionGame[]>([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState<SelectionGame[]>([]);

  // os ids a excluir mudam a cada render do pai; guardo o valor de quando o painel abriu
  const excludeRef = useRef<number[]>([]);

  const fetchPage = useCallback(
    async (targetPage: number, term: string, replace: boolean) => {
      setLoading(true);
      try {
        const { ok, data } = await getSelectionAvailableGames({
          lottery_id: lotteryId,
          dezenas,
          selection_uuid: selectionUuid,
          exclude_ids: excludeRef.current,
          search: term || undefined,
          page: targetPage,
          per_page: PER_PAGE,
        });

        if (ok && data?.response) {
          const pageData = data.response;
          setGames((current) => (replace ? pageData.data : [...current, ...pageData.data]));
          setPage(pageData.current_page);
          setLastPage(pageData.last_page);
          setTotal(pageData.total);
        }
      } finally {
        setLoading(false);
      }
    },
    [lotteryId, dezenas, selectionUuid]
  );

  // abre limpo e busca a primeira página
  useEffect(() => {
    if (!open) return;

    excludeRef.current = excludeIds;
    setSearch("");
    setAppliedSearch("");
    setPicked([]);
    setGames([]);
    fetchPage(1, "", true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lotteryId, dezenas]);

  // busca pelo Nº do jogo, com um respiro para não consultar a cada tecla
  useEffect(() => {
    if (!open || search === appliedSearch) return;

    const timer = setTimeout(() => {
      setAppliedSearch(search);
      fetchPage(1, search, true);
    }, 350);

    return () => clearTimeout(timer);
  }, [search, appliedSearch, open, fetchPage]);

  const isPicked = (id: number) => picked.some((game) => game.id === id);

  const toggle = (game: SelectionGame) => {
    setPicked((current) => {
      if (current.some((item) => item.id === game.id)) return current.filter((item) => item.id !== game.id);
      // com uma vaga só (Trocar jogo) a nova escolha substitui a anterior
      if (maxSelect === 1) return [game];
      if (current.length >= maxSelect) return current;
      return [...current, game];
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 bg-white p-0 dark:bg-zinc-950 sm:max-w-md"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader className="space-y-1 border-b p-5 pr-12">
          <SheetTitle>Banco de jogos · {pad2(dezenas)} dezenas</SheetTitle>
          <SheetDescription>Só aparecem jogos que ainda não foram selecionados.</SheetDescription>
        </SheetHeader>

        <div className="space-y-3 border-b p-4">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por número do jogo"
              inputMode="numeric"
              className="pl-8"
            />
          </div>
          <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
            <Info className="h-4 w-4 shrink-0" />
            {maxSelect === 1
              ? `Selecione 1 jogo para o ${bolaoLabel}.`
              : `Selecione até ${maxSelect} jogos para o ${bolaoLabel}.`}
          </div>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          {games.length === 0 && loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : games.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              Nenhum jogo disponível com {dezenas} dezenas.
              <br />
              Cadastre mais jogos no Banco de Jogos.
            </p>
          ) : (
            <>
              {games.map((game) => {
                const checked = isPicked(game.id);
                const blocked = !checked && maxSelect > 1 && picked.length >= maxSelect;

                return (
                  <label
                    key={game.id}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition",
                      checked
                        ? "border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30"
                        : "hover:bg-muted/50",
                      blocked && "cursor-not-allowed opacity-50"
                    )}
                  >
                    <Checkbox
                      checked={checked}
                      disabled={blocked}
                      onCheckedChange={() => toggle(game)}
                      className="mt-0.5"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">Nº {String(game.game_number).padStart(3, "0")}</p>
                      <p className="break-words font-mono text-xs leading-relaxed text-muted-foreground">
                        {game.numbers.map((number) => pad2(number)).join(" ")}
                      </p>
                    </div>
                  </label>
                );
              })}

              {page < lastPage && (
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-blue-600"
                  disabled={loading}
                  onClick={() => fetchPage(page + 1, appliedSearch, false)}
                >
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Carregar mais ({games.length} de {total})
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t p-4">
          <span className="text-sm text-muted-foreground">
            {picked.length} {picked.length === 1 ? "jogo selecionado" : "jogos selecionados"}
          </span>
          <Button
            type="button"
            className={appDialogPrimaryButton}
            disabled={picked.length === 0}
            onClick={() => {
              onConfirm(picked);
              onOpenChange(false);
            }}
          >
            {mode === "replace" ? "Trocar jogo" : "Adicionar ao bolão"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
